import { useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChatResponseType, getChatContextMetadata, getSegmentFromChatContext } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveApplicationIdentifierNullSafe } from '@trinity/chat-components/containers/UserApplications/useActiveApplicationIdentifier';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import {
  ActiveChatSessionContext,
  ChatHintsLoadingContext,
  ChatInitialResponseContext,
  ChatSessionFiltersContext,
} from './context';

type Props = {
  children: React.ReactNode;
};

export const ChatHintsContainer = (props: Props) => {
  const chat = useContext(ActiveChatSessionContext);
  const filters = useContext(ChatSessionFiltersContext);
  const brandIdentifier = useActiveBrandIdentifierNullSafe();
  const applicationIdentifier = useActiveApplicationIdentifierNullSafe();
  const client = useChatApiClient();

  // The server picks the segment start screen from `metadata.segment` on the context. A segment
  // selected under Tags must drive it too, and wins over a segment left on the chat context so the
  // start screen never shows the previously selected segment. A user-picked (removable) segment
  // takes precedence over a locked one.
  const segmentTags = (filters.tags ?? []).filter((t) => !!t.metadata?.segmentIdentifier);
  const segmentTag = segmentTags.find((t) => t.metadata?.canRemove !== false) ?? segmentTags[0];
  const segmentTagId = segmentTag?.metadata?.segmentIdentifier ?? undefined;
  const segmentTagName = segmentTag?.name;
  const context = useMemo(() => {
    if (!segmentTagId || !segmentTagName || getSegmentFromChatContext(chat.context)?.id === segmentTagId) {
      return chat.context;
    }
    return {
      ...chat.context,
      metadata: {
        ...(getChatContextMetadata(chat.context) ?? {}),
        segment: { id: segmentTagId, name: segmentTagName },
      },
    } as typeof chat.context;
  }, [chat.context, segmentTagId, segmentTagName]);

  const query = useQuery({
    queryKey: ['chat-hints', chat.identifier, context, brandIdentifier, applicationIdentifier],
    queryFn: () => {
      return client.chat.getChatInitialMessage({
        context,
        brandIdentifier,
        applicationIdentifier,
      });
    },
    refetchOnMount: 'always',
    initialData: {
      type: ChatResponseType.InitialGreeting,
      value: '',
      data: {
        hints: [],
      },
    },
  });

  const loading = useMemo(() => {
    return query.isFetching;
  }, [query.isFetching]);

  return (
    <ChatInitialResponseContext.Provider value={query.data}>
      <ChatHintsLoadingContext.Provider value={loading}>{props.children}</ChatHintsLoadingContext.Provider>
    </ChatInitialResponseContext.Provider>
  );
};
